import React, { useState, useEffect } from 'react';
import { History, Plus, Minus, Search, Eye } from 'lucide-react';
import { pointsService } from '../../services/pointsService';
import { customerService } from '../../services/customerService';
import { formatters } from '../../utils/formatters';
import { useRedemptions } from '../../hooks/useRewards';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

const CustomerPointsHistory = () => {
  const { user } = useAuth();
  const { redemptions, fetchCompanyRedemptions } = useRedemptions();
  
  const [customerDocument, setCustomerDocument] = useState('');
  const [customer, setCustomer] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [customerRedemptions, setCustomerRedemptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  
  // Estados para informações de expiração
  const [expirationInfo, setExpirationInfo] = useState({
    validPoints: 0,
    pointsToExpire: [],
    expiredPoints: []
  });

  const handleSearch = async () => {
    if (!customerDocument.trim()) {
      setError('Digite um CPF ou CNPJ para buscar');
      return;
    }

    // Limpar documento para validação
    const cleanDocument = customerDocument.replace(/[^\d]/g, '');
    
    if (cleanDocument.length === 0) {
      setError('Documento não pode estar vazio');
      return;
    }
    
    if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
      setError(`Documento deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ). Você digitou ${cleanDocument.length} dígitos.`);
      return;
    }

    setLoading(true);
    setError('');
    setCustomer(null);
    setPointsHistory([]);
    setCustomerRedemptions([]);
    setExpirationInfo({
      validPoints: 0,
      pointsToExpire: [],
      expiredPoints: []
    });

    try {
      // Buscar cliente por documento
      const customers = await customerService.getByDocument(cleanDocument);
      const foundCustomer = customers.find(c => c.companyId === user.companyId);
      
      if (!foundCustomer) {
        setError('Cliente não encontrado nesta empresa');
        return;
      }

      setCustomer(foundCustomer);

      // Buscar histórico de pontos
      const history = await pointsService.getCustomerHistory(foundCustomer.id);
      setPointsHistory(history);

      // Buscar resgates do cliente
      await fetchCompanyRedemptions(user.companyId);
      const customerRedemptionsList = redemptions.filter(r => r.customerId === foundCustomer.id);
      setCustomerRedemptions(customerRedemptionsList);

      // Buscar informações de expiração de pontos
      try {
        const validPointsInfo = await pointsService.getValidCustomerPoints(foundCustomer.id, user.companyId);
        const expiredPointsInfo = await pointsService.getExpiredPoints(foundCustomer.id, user.companyId);
        
        setExpirationInfo({
          validPoints: validPointsInfo.validPoints,
          pointsToExpire: validPointsInfo.pointsToExpire,
          expiredPoints: expiredPointsInfo
        });
      } catch (expirationError) {
        console.error('Erro ao buscar informações de expiração:', expirationError);
        // Manter valores padrão se não conseguir buscar
        setExpirationInfo({
          validPoints: foundCustomer.points || 0,
          pointsToExpire: [],
          expiredPoints: []
        });
      }

    } catch (err) {
      setError('Erro ao buscar dados do cliente: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Data não disponível';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  const getTransactionTypeLabel = (type) => {
    const types = {
      add: 'Pontos Adicionados',
      redeem: 'Pontos Resgatados',
      adjustment: 'Ajuste Manual',
      referral_bonus: 'Bônus Indicação',
      welcome_bonus: 'Bônus Boas-vindas',
      expired: 'Pontos Expirados'
    };
    return types[type] || type;
  };

  const getRedemptionStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600',
      approved: 'text-blue-600',
      completed: 'text-green-600',
      rejected: 'text-red-600'
    };
    return colors[status] || 'text-gray-600';
  };

  const getRedemptionStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      approved: 'Aprovado',
      completed: 'Concluído',
      rejected: 'Rejeitado'
    };
    return labels[status] || status;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <History className="mr-2" />
          Histórico de Pontos do Cliente
        </h1>
        <p className="text-gray-600 mt-1">
          Consulte todo o histórico de pontos e resgates de um cliente
        </p>
      </div>

      {/* Busca por Cliente */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CPF/CNPJ do Cliente
            </label>
            <input
              type="text"
              value={customerDocument}
              onChange={(e) => {
                // Permitir apenas números e limitar a 14 dígitos (CNPJ)
                const value = e.target.value.replace(/[^\d]/g, '').slice(0, 14);
                setCustomerDocument(value);
              }}
              placeholder="00000000000 (CPF) ou 00000000000000 (CNPJ)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              maxLength="14"
            />
            <p className="text-xs text-gray-500 mt-1">
              Digite 11 números para CPF ou 14 números para CNPJ (sem formatação)
            </p>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Search size={16} />
              Buscar
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {loading && <LoadingSpinner />}

      {customer && (
        <div className="space-y-6">
          {/* Informações do Cliente */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações do Cliente</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nome</p>
                <p className="font-medium">{customer.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CPF/CNPJ</p>
                <p className="font-medium">{formatters.formatCpfOrCnpj(customer.cpf)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pontos Atuais</p>
                <p className="font-bold text-2xl text-blue-600">{customer.points || 0}</p>
              </div>
            </div>
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-center">
                <Plus className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-600">Total Ganho</p>
                  <p className="text-2xl font-bold text-green-900">
                    {pointsHistory.reduce((sum, h) => sum + (h.type === 'add' ? h.points : 0), 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-6">
              <div className="flex items-center">
                <Minus className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-red-600">Total Resgatado</p>
                  <p className="text-2xl font-bold text-red-900">
                    {
                      // Somar resgates da tabela redemptions (concluídos)
                      customerRedemptions.reduce((sum, r) => sum + (r.status === 'completed' ? r.pointsUsed : 0), 0) +
                      // Somar resgates forçados do histórico de pontos (valores negativos)
                      Math.abs(pointsHistory.reduce((sum, h) => sum + (h.type === 'redeem' ? h.points : 0), 0))
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center">
                <History className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-600">Total de Resgates</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {
                      // Contar resgates da tabela redemptions
                      customerRedemptions.length +
                      // Contar resgates forçados do histórico de pontos
                      pointsHistory.filter(h => h.type === 'redeem').length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Detalhamento dos Resgates */}
          {(customerRedemptions.length > 0 || pointsHistory.filter(h => h.type === 'redeem').length > 0) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Detalhamento dos Resgates</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Resgates via Sistema */}
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{customerRedemptions.length}</div>
                  <div className="text-sm text-blue-800 font-medium mt-1">Via Sistema</div>
                  <div className="text-xs text-blue-600 mt-1">
                    {customerRedemptions.reduce((sum, r) => sum + (r.status === 'completed' ? r.pointsUsed : 0), 0)} pontos
                  </div>
                </div>

                {/* Resgates Forçados */}
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{pointsHistory.filter(h => h.type === 'redeem').length}</div>
                  <div className="text-sm text-red-800 font-medium mt-1">Forçados/Manuais</div>
                  <div className="text-xs text-red-600 mt-1">
                    {Math.abs(pointsHistory.reduce((sum, h) => sum + (h.type === 'redeem' ? h.points : 0), 0))} pontos
                  </div>
                </div>

                {/* Total Geral */}
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {customerRedemptions.length + pointsHistory.filter(h => h.type === 'redeem').length}
                  </div>
                  <div className="text-sm text-purple-800 font-medium mt-1">Total Geral</div>
                  <div className="text-xs text-purple-600 mt-1">
                    {
                      customerRedemptions.reduce((sum, r) => sum + (r.status === 'completed' ? r.pointsUsed : 0), 0) +
                      Math.abs(pointsHistory.reduce((sum, h) => sum + (h.type === 'redeem' ? h.points : 0), 0))
                    } pontos
                  </div>
                </div>
              </div>

              {/* Legenda */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-gray-900">Via Sistema</div>
                      <div className="text-gray-600">Resgates solicitados pelo cliente e processados pela empresa</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                    <div>
                      <div className="font-medium text-gray-900">Forçados/Manuais</div>
                      <div className="text-gray-600">Resgates processados diretamente pela empresa via "Forçar Resgate"</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Informações de Expiração de Pontos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações de Expiração de Pontos</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Pontos Válidos */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-green-600">Pontos Válidos</p>
                    <p className="text-2xl font-bold text-green-900">{expirationInfo.validPoints}</p>
                  </div>
                </div>
              </div>

              {/* Pontos que Vão Expirar */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-yellow-600">Prestes a Expirar</p>
                    <p className="text-2xl font-bold text-yellow-900">
                      {expirationInfo.pointsToExpire.reduce((sum, item) => sum + item.points, 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pontos Expirados */}
              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-red-600">Pontos Expirados</p>
                    <p className="text-2xl font-bold text-red-900">
                      {expirationInfo.expiredPoints.reduce((sum, item) => sum + item.points, 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Detalhamento dos Pontos que Vão Expirar */}
            {expirationInfo.pointsToExpire.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 mb-3">Pontos com Expiração Próxima</h3>
                <div className="space-y-2">
                  {expirationInfo.pointsToExpire.slice(0, 5).map((item, index) => {
                    const daysUntilExpiration = Math.ceil((item.expiresAt - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={index} className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center space-x-3">
                          <div className="font-medium text-yellow-800">{item.points} pontos</div>
                          <div className="text-sm text-yellow-600">
                            {item.reason || 'Pontos adicionados'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-yellow-800">
                            Expira em {daysUntilExpiration} dia{daysUntilExpiration !== 1 ? 's' : ''}
                          </div>
                          <div className="text-xs text-yellow-600">
                            {item.expiresAt.toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {expirationInfo.pointsToExpire.length > 5 && (
                    <div className="text-center p-2 text-sm text-gray-500">
                      E mais {expirationInfo.pointsToExpire.length - 5} lote{expirationInfo.pointsToExpire.length - 5 !== 1 ? 's' : ''} de pontos...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detalhamento dos Pontos Expirados */}
            {expirationInfo.expiredPoints.length > 0 && (
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Pontos Expirados Recentemente</h3>
                <div className="space-y-2">
                  {expirationInfo.expiredPoints.slice(0, 5).map((item, index) => {
                    const expiresAt = item.expiresAt?.toDate ? item.expiresAt.toDate() : (item.expiresAt ? new Date(item.expiresAt) : null);
                    return (
                      <div key={index} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center space-x-3">
                          <div className="font-medium text-red-800">{item.points} pontos</div>
                          <div className="text-sm text-red-600">
                            {item.reason || 'Pontos adicionados'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-red-800">
                            Expirou em
                          </div>
                          <div className="text-xs text-red-600">
                            {expiresAt ? expiresAt.toLocaleDateString('pt-BR') : 'Data não disponível'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {expirationInfo.expiredPoints.length > 5 && (
                    <div className="text-center p-2 text-sm text-gray-500">
                      E mais {expirationInfo.expiredPoints.length - 5} lote{expirationInfo.expiredPoints.length - 5 !== 1 ? 's' : ''} de pontos expirados...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mensagem quando não há informações de expiração */}
            {expirationInfo.pointsToExpire.length === 0 && expirationInfo.expiredPoints.length === 0 && (
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <p className="text-gray-600">
                  Não há informações de expiração disponíveis ou a empresa não configurou expiração de pontos.
                </p>
              </div>
            )}
          </div>

          {/* Histórico de Pontos */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Histórico de Movimentação de Pontos</h2>
            </div>
            <div className="overflow-x-auto">
              {pointsHistory.length === 0 ? (
                <div className="p-8 text-center">
                  <History className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nenhuma movimentação encontrada
                  </h3>
                  <p className="text-gray-600">
                    Este cliente ainda não possui histórico de pontos
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data/Hora
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pontos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Observações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pointsHistory.map((history, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(history.createdAt)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${
                            (history.type === 'add' || history.type === 'referral_bonus' || history.type === 'welcome_bonus') 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {(history.type === 'add' || history.type === 'referral_bonus' || history.type === 'welcome_bonus') 
                              ? <Plus size={12} /> 
                              : <Minus size={12} />}
                            {getTransactionTypeLabel(history.type)}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium text-right ${
                          (history.type === 'add' || history.type === 'referral_bonus' || history.type === 'welcome_bonus') 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {(history.type === 'add' || history.type === 'referral_bonus' || history.type === 'welcome_bonus') 
                            ? '+' 
                            : '-'}{Math.abs(history.points)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {history.reason || history.description || 'Sem observações'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Histórico de Resgates */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Histórico de Resgates</h2>
              <button
                onClick={() => setShowModal(true)}
                className="text-blue-600 hover:text-blue-900 flex items-center gap-1 text-sm"
              >
                <Eye size={16} />
                Ver Detalhes
              </button>
            </div>
            <div className="overflow-x-auto">
              {customerRedemptions.length === 0 ? (
                <div className="p-8 text-center">
                  <Minus className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nenhum resgate encontrado
                  </h3>
                  <p className="text-gray-600">
                    Este cliente ainda não solicitou nenhum resgate
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data Solicitação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prêmio
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pontos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customerRedemptions.slice(0, 5).map((redemption) => (
                      <tr key={redemption.id}>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(redemption.requestedAt)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {redemption.rewardName}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-red-600 text-right">
                          -{redemption.pointsUsed}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-medium ${getRedemptionStatusColor(redemption.status)}`}>
                            {getRedemptionStatusLabel(redemption.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal com Detalhes Completos dos Resgates */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Histórico Completo de Resgates"
      >
        <div className="max-h-96 overflow-y-auto">
          {customerRedemptions.length === 0 ? (
            <p className="text-gray-600 text-center py-8">Nenhum resgate encontrado</p>
          ) : (
            <div className="space-y-4">
              {customerRedemptions.map((redemption) => (
                <div key={redemption.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">{redemption.rewardName}</h4>
                    <span className={`text-sm font-medium ${getRedemptionStatusColor(redemption.status)}`}>
                      {getRedemptionStatusLabel(redemption.status)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Pontos usados:</strong> {redemption.pointsUsed}</p>
                    <p><strong>Solicitado em:</strong> {formatDate(redemption.requestedAt)}</p>
                    {redemption.approvedAt && (
                      <p><strong>Aprovado em:</strong> {formatDate(redemption.approvedAt)}</p>
                    )}
                    {redemption.completedAt && (
                      <p><strong>Concluído em:</strong> {formatDate(redemption.completedAt)}</p>
                    )}
                    {redemption.rejectedAt && (
                      <p><strong>Rejeitado em:</strong> {formatDate(redemption.rejectedAt)}</p>
                    )}
                    {redemption.notes && (
                      <p><strong>Observações:</strong> {redemption.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CustomerPointsHistory;
